import React, { createContext, useContext, useEffect, useState } from 'react';
import { once, on, removeListener, run } from '../helper/electron';
import { performanceLogger } from '../utils/performanceLogger';
import isElectron from '../utils/isElectron';

// Shared type for statistics
interface TrackStatistics {
  totalFiles: number;
  totalArtists: number;
  totalAlbums: number;
  totalGenres: number;
  duplicates: number;
  // New metrics
  hasSerato?: boolean;
  missingGenreCount?: number;
  missingYearCount?: number;
  missingTitleOrArtistCount?: number;
  exactDuplicateCount?: number;
  lowBitrateCount?: number;
  missingBpmCount?: number;
  missingKeyCount?: number;
  missingFileCount?: number;
  unplayedCount?: number;
}

interface LibraryStatsContextType {
  stats: TrackStatistics | null;
  healthScore: number | null;
  loading: boolean;
  refreshStats: () => void;
}

export const LibraryStatsContext = createContext<
  LibraryStatsContextType | undefined
>(undefined);

export const useLibraryStats = () => {
  const context = useContext(LibraryStatsContext);
  if (context === undefined) {
    throw new Error(
      'useLibraryStats must be used within a LibraryStatsProvider',
    );
  }
  return context;
};

interface LibraryStatsProviderProps {
  children: React.ReactNode;
}

export const LibraryStatsProvider: React.FC<LibraryStatsProviderProps> = ({
  children,
}) => {
  const [stats, setStats] = useState<TrackStatistics | null>(null);
  const [healthScore, setHealthScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const refreshStats = () => {
    if (!isElectron()) {
      return;
    }
    performanceLogger.startTimer('LibraryStatsContext - Refresh Stats');
    setLoading(true);
    setRetryCount(0);

    // Set up fresh listeners for each request
    once('track/get-statistics', handleStatsReceived);
    once('get-last-health-report', handleHealthReceived);

    console.log('LibraryStatsContext: Requesting track statistics...');
    run('track/get-statistics');
    run('get-last-health-report');
  };

  const handleStatsReceived = (s: TrackStatistics) => {
    console.log('LibraryStatsContext: Received stats:', s);
    setStats(s);
    // Update localStorage for fallback usage
    localStorage.setItem(
      'hasScannedTracks',
      s.totalFiles > 0 ? 'true' : 'false',
    );
    if (healthScore !== null) {
      setLoading(false);
      performanceLogger.endTimer('LibraryStatsContext - Refresh Stats', {
        totalFiles: s.totalFiles,
        success: true,
      });
    }
  };

  const handleHealthReceived = (r: { score: number | null }) => {
    console.log('LibraryStatsContext: Received health score:', r);
    setHealthScore(r.score);
    if (stats !== null) {
      setLoading(false);
      performanceLogger.endTimer('LibraryStatsContext - Refresh Stats', {
        healthScore: r.score,
        success: true,
      });
    }
  };

  useEffect(() => {
    if (!isElectron()) {
      setLoading(false);
      setStats(null);
      setHealthScore(null);
      return;
    }
    let isMounted = true;
    let statsReceived = false;
    let healthReceived = false;

    const handleStatsReceivedOnce = (s: TrackStatistics) => {
      if (!isMounted || statsReceived) return;
      statsReceived = true;
      console.log('LibraryStatsContext: Received stats:', s);
      setStats(s);
      // Update localStorage for fallback usage
      localStorage.setItem(
        'hasScannedTracks',
        s.totalFiles > 0 ? 'true' : 'false',
      );
      if (healthReceived) {
        setLoading(false);
        performanceLogger.endTimer('LibraryStatsContext - Initial Load', {
          totalFiles: s.totalFiles,
          success: true,
        });
      }
    };

    const handleHealthReceivedOnce = (r: { score: number | null }) => {
      if (!isMounted || healthReceived) return;
      healthReceived = true;
      console.log('LibraryStatsContext: Received health score:', r);
      setHealthScore(r.score);
      if (statsReceived) {
        setLoading(false);
        performanceLogger.endTimer('LibraryStatsContext - Initial Load', {
          healthScore: r.score,
          success: true,
        });
      }
    };

    const requestData = () => {
      if (!isMounted) return;

      performanceLogger.startTimer('LibraryStatsContext - Initial Load');

      // Set up fresh listeners for each request
      once('track/get-statistics', handleStatsReceivedOnce);
      once('get-last-health-report', handleHealthReceivedOnce);

      console.log('LibraryStatsContext: Requesting track statistics...');
      run('track/get-statistics');
      run('get-last-health-report');
    };

    // Initial request
    requestData();

    // Fallback timeout with longer delay
    const fallbackTimeout = setTimeout(() => {
      if (!isMounted || (statsReceived && healthReceived)) return;

      console.log(
        'LibraryStatsContext: Fallback timeout triggered, retrying...',
      );
      if (retryCount < 1) {
        setRetryCount((prev) => prev + 1);
        requestData(); // Will set up new listeners
      } else {
        console.warn(
          'LibraryStatsContext: Failed to load statistics after retries',
        );
        if (!statsReceived) {
          setStats({
            totalFiles: 0,
            totalArtists: 0,
            totalAlbums: 0,
            totalGenres: 0,
            duplicates: 0,
          });
        }
        setLoading(false);
      }
    }, 5000);

    // Set up persistent listener for background updates
    const handlePersistentStats = (s: TrackStatistics) => {
      if (isMounted) {
        console.log(
          'LibraryStatsContext: Received persistent stats update:',
          s,
        );
        setStats(s);
        // Update localStorage for fallback usage
        localStorage.setItem(
          'hasScannedTracks',
          s.totalFiles > 0 ? 'true' : 'false',
        );
      }
    };
    on('track/get-statistics', handlePersistentStats);

    return () => {
      isMounted = false;
      clearTimeout(fallbackTimeout);
      removeListener('track/get-statistics');
    };
  }, [retryCount]);

  const value: LibraryStatsContextType = {
    stats,
    healthScore,
    loading,
    refreshStats,
  };

  return (
    <LibraryStatsContext.Provider value={value}>
      {children}
    </LibraryStatsContext.Provider>
  );
};
